import { useState } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { apiPost } from '@/services/api';
import { logger } from '@/lib/logger';
import logoUrl from '@assets/logo_square_1753226114988.png';

export default function Login() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast({ title: "Email required", description: "Please enter your email address.", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      logger.log('📧 Requesting passcode for:', email);
      await apiPost('/api/auth/otp/request', { email }, false);
      logger.log('✅ Passcode requested successfully');

      toast({ title: "Code sent", description: "Check your email for the verification code." });
      sessionStorage.setItem('otp_email', email);
      setLocation('/verify-otp');
    } catch (error: any) {
      logger.error('Request passcode error:', error);
      toast({
        title: "Failed to send code",
        description: error.message || "Please check your email and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="bg-[#1f2937] text-white py-16">
        <div className="max-w-7xl mx-auto px-8">
          <div className="flex items-center justify-center gap-3">
            <img src={logoUrl} alt="Wellbound+" className="h-10 w-10" />
            <h1 className="text-2xl font-semibold">Wellbound+</h1>
          </div>
          <p className="text-white/80 text-base mt-2 text-center">Admin Tools</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 pt-20">
        <div className="max-w-md w-full">
          <Card className="shadow-md border-0">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-slate-900">Sign in to your account</h2>
                <p className="mt-1 text-sm text-slate-500">Enter your email and we'll send you a verification code</p>
              </div>
              <form onSubmit={handleSendCode} className="space-y-5">
                <div>
                  <Label htmlFor="email" className="block text-sm font-normal text-slate-700 mb-1.5">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    disabled={loading}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !email}
                  className="w-full bg-[#595ebe] hover:bg-[#595ebe]/90 text-white py-2.5 px-4 rounded font-medium transition-colors"
                >
                  {loading ? 'Sending code...' : 'Send verification code'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
